import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { portfolioData } from '../data/mock';

const HeroSection = () => {
  const { identity } = portfolioData;
  const [showTagline, setShowTagline] = useState(false);
  const [displayedTagline, setDisplayedTagline] = useState('');
  const [showScrollCue, setShowScrollCue] = useState(false);
  
  useEffect(() => {
    if (showTagline) {
      let index = 0;
      const interval = setInterval(() => {
        if (index <= identity.tagline.length) {
          setDisplayedTagline(identity.tagline.slice(0, index));
          index++;
        } else {
          clearInterval(interval);
          setTimeout(() => setShowScrollCue(true), 500);
        }
      }, 45);
      return () => clearInterval(interval);
    }
  }, [showTagline, identity.tagline]);
  
  const nameLetters = identity.name.split('');
  
  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 relative">
      {/* Symbol */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="mb-8 sm:mb-10"
      >
        <motion.img
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.08, filter: 'drop-shadow(0 0 30px rgba(200, 215, 235, 0.5))' }}
          src="https://customer-assets.emergentagent.com/job_9473450d-8fdf-465d-8892-552dd8ecfad1/artifacts/hz3lxj60_image.png"
          alt="Symbol"
          className="w-32 h-32 sm:w-40 sm:h-40 md:w-44 md:h-44 object-contain cursor-pointer"
          style={{ filter: 'drop-shadow(0 0 20px rgba(200, 215, 235, 0.3))' }}
        />
      </motion.div>
      
      {/* Name */}
      <div className="text-center mb-3 px-2">
        <h1 
          className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl tracking-wide"
          style={{ fontFamily: 'Cinzel, serif', color: 'rgba(240, 245, 255, 0.95)', fontWeight: 400 }}
        >
          {nameLetters.map((letter, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06, ease: "easeOut" }}
              onAnimationComplete={() => {
                if (index === nameLetters.length - 1) {
                  setTimeout(() => setShowTagline(true), 600);
                }
              }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </motion.span>
          ))}
        </h1>
      </div>
      
      {/* Alias */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: nameLetters.length * 0.06 + 0.6 }}
        className="text-xs sm:text-sm tracking-widest mb-8 sm:mb-10"
        style={{ fontFamily: 'DM Mono, monospace', color: 'rgba(150, 170, 200, 0.7)', letterSpacing: '0.35em', fontSize: '0.55em' }}
      >
        {identity.alias}
      </motion.p>
      
      {/* Tagline */}
      <div className="h-8 max-w-2xl text-center px-4 sm:px-6">
        {showTagline && (
          <p
            className="text-sm sm:text-base md:text-lg"
            style={{ fontFamily: 'EB Garamond, serif', color: 'rgba(200, 215, 235, 0.85)', fontStyle: 'italic' }}
          >
            "{displayedTagline}"
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: displayedTagline.length < identity.tagline.length ? Infinity : 3 }}
            >
              |
            </motion.span>
          </p>
        )}
      </div>
      
      {/* Scroll cue */}
      {showScrollCue && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-3"
          >
            <span style={{ fontFamily: 'DM Mono, monospace', color: 'rgba(150, 170, 200, 0.6)', letterSpacing: '0.2em', fontSize: '0.75rem' }}>
              scroll to explore
            </span>
            <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
              <path d="M2 2L10 10L18 2" stroke="rgba(150, 170, 200, 0.5)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default HeroSection;
